import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Building2, Plane, Heart, Cpu, TrendingUp, Tv, LayoutGrid } from 'lucide-react';

const OurFamilySection = () => {
  const parentBrand = {
    name: 'Prestigious Companies',
    tagline: 'One Prestigious Mission, Many Paths to Empowerment',
    description: 'A family of brands united by purpose — driving impact across nonprofit outreach, travel experiences, marketing innovation, technology solutions, and financial empowerment.',
    link: 'https://prestigiouscompanies.org',
    divisions: '8 Divisions',
    lives: '10K+ Lives Impacted',
  };

  const brands = [
    {
      icon: Heart,
      name: 'The Foundation',
      type: '501(c)(3) Nonprofit',
      description: 'Helping families, veterans, and individuals in need through community-driven outreach.',
      link: '/',
      current: true,
    },
    {
      icon: Plane,
      name: 'Prestigious Travel Club',
      type: 'Travel & Lifestyle',
      description: 'Exclusive travel experiences, personal development, and lifestyle benefits for families.',
      link: 'https://prestigioustravelclub.org',
    },
    {
      icon: TrendingUp,
      name: 'Prestigious Marketing',
      type: 'Marketing Agency',
      description: 'Full-service marketing helping businesses grow and build meaningful relationships.',
      link: 'https://pmllc.org',
    },
    {
      icon: Cpu,
      name: 'Prestigious Apps',
      type: 'Technology',
      description: 'Smart digital tools that simplify business management and client communications.',
      link: null,
      comingSoon: true,
    },
    {
      icon: Building2,
      name: 'Easy Quick Fund',
      type: 'Financial Services',
      description: 'Simple, flexible funding solutions for families and businesses.',
      link: 'https://easyquickfund.com',
    },
    {
      icon: Tv,
      name: 'Prestigious TV',
      type: 'Streaming Platform',
      description: '50,000+ channels worldwide — live TV, movies, sports, and global entertainment.',
      link: 'https://prestigious-tv.com',
      featured: true,
    },
  ];

  const openGhostChat = () => {
    const ghostButton = document.querySelector('[data-testid="ghost-assistant-button"]');
    if (ghostButton) {
      ghostButton.click();
    }
  };

  return (
    <section id="family" className="py-24 bg-slate-50" data-testid="our-family-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        
        {/* Parent Brand Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <a 
            href={parentBrand.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block group"
          >
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#1E3A8A] mb-4 group-hover:text-amber-600 transition-colors">
              — Prestigious Companies —
            </p>
          </a>
          <h2 className="text-4xl sm:text-5xl font-medium text-[#0B1120] tracking-tight mb-4">
            Our <span className="text-[#1E3A8A]">Family</span> of Brands
          </h2>
          <p className="text-lg text-slate-600 font-light max-w-3xl mx-auto mb-8">
            {parentBrand.tagline}
          </p>
          
          {/* Stats */}
          <div className="flex items-center justify-center gap-8 mb-8">
            <div className="text-center">
              <p className="text-3xl font-bold text-[#1E3A8A] font-serif">{parentBrand.divisions}</p>
              <p className="text-sm text-slate-500">Business Units</p>
            </div>
            <div className="w-px h-12 bg-slate-300" />
            <div className="text-center">
              <p className="text-3xl font-bold text-[#1E3A8A] font-serif">{parentBrand.lives}</p>
              <p className="text-sm text-slate-500">And Growing</p>
            </div>
          </div>

          <a
            href={parentBrand.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#1E3A8A] hover:text-amber-600 font-medium transition-colors"
          >
            Visit Prestigious Companies
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </motion.div>

        {/* Brand Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {brands.map((brand, idx) => (
            <motion.div
              key={brand.name}
              className={`relative rounded-2xl bg-white border-2 overflow-hidden group transition-all duration-300 ${
                brand.current 
                  ? 'border-[#1E3A8A] shadow-lg' 
                  : 'border-slate-200 hover:border-[#1E3A8A]/50 hover:shadow-md'
              }`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
            >
              {/* Badges */}
              <div className="absolute top-4 right-4 flex gap-2">
                {brand.current && (
                  <span className="px-2 py-1 bg-[#1E3A8A] text-white text-xs font-bold rounded-full">
                    You're Here
                  </span>
                )}
                {brand.featured && (
                  <span className="px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded-full">
                    Featured
                  </span>
                )}
                {brand.comingSoon && (
                  <span className="px-2 py-1 bg-slate-400 text-white text-xs font-bold rounded-full">
                    Coming Soon
                  </span>
                )}
              </div>

              <div className="p-6">
                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  brand.current ? 'bg-[#1E3A8A]' : 'bg-slate-100 group-hover:bg-[#DBEAFE]'
                } transition-colors`}>
                  <brand.icon className={`w-6 h-6 ${brand.current ? 'text-white' : 'text-[#1E3A8A]'}`} />
                </div>

                {/* Type */}
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  {brand.type}
                </span>

                {/* Name */}
                <h3 className="text-xl font-semibold text-slate-800 mt-1 mb-2 font-serif">
                  {brand.name}
                </h3>

                {/* Description */}
                <p className="text-slate-600 font-light text-sm leading-relaxed mb-4">
                  {brand.description}
                </p>

                {/* Link */}
                {brand.current ? (
                  <span className="text-[#1E3A8A] font-medium text-sm">Currently Viewing</span>
                ) : brand.comingSoon ? (
                  <span className="text-slate-400 font-medium text-sm">Coming Soon</span>
                ) : (
                  <a 
                    href={brand.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[#1E3A8A] hover:text-amber-600 font-medium text-sm transition-colors"
                  >
                    Visit Site
                    <ArrowUpRight className="w-3 h-3" />
                  </a>
                )}
              </div>

              {/* Bottom accent */}
              <div className={`h-1 ${brand.current ? 'bg-[#1E3A8A]' : 'bg-slate-100 group-hover:bg-[#1E3A8A]/30'} transition-colors`} />
            </motion.div>
          ))}
        </div>

        {/* Ghost Card - Special Treatment */}
        <motion.div
          className="mt-8 rounded-3xl bg-[#0B1120] overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8 lg:p-12 items-center">
            <div>
              <span className="inline-block px-3 py-1 bg-amber-500/20 text-amber-400 text-xs font-bold rounded-full mb-4">
                Proprietary Technology
              </span>
              <h3 className="text-3xl font-semibold text-white mb-4 font-serif">
                Ghost™ Assistant
              </h3>
              <p className="text-slate-300 font-light leading-relaxed mb-6">
                An advanced AI platform built on a foundation of service, discipline, and the 
                unwavering commitment to helping others move forward. A tribute to those who 
                have served and sacrificed.
              </p>
              <button
                onClick={openGhostChat}
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-[#0B1120] font-semibold px-6 py-3 rounded-full transition-colors"
              >
                Chat with Ghost
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute inset-0 bg-amber-500/20 rounded-full blur-3xl" />
                <img 
                  src="https://customer-assets.emergentagent.com/job_6b3489be-920b-4f06-be49-83f9903cfac6/artifacts/v5rprkkl_image.png"
                  alt="Ghost"
                  className="relative w-48 h-48 object-contain"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tagline */}
        <motion.p 
          className="text-center mt-12 text-slate-500 text-sm"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <a 
            href="https://prestigiouscompanies.org"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#1E3A8A] transition-colors"
          >
            Prestigious Companies™
          </a>
          {' '}— Travel. Give. Transform.
        </motion.p>
      </div>
    </section>
  );
};

export default OurFamilySection;
